import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import bannerDesktop from "@/assets/banner-desktop.mp4.asset.json";
import bannerMobile from "@/assets/banner-mobile.mp4.asset.json";
import bannerPoster from "@/assets/banner-poster.webp.asset.json";
import couplesDesktop from "@/assets/intro/couples-desktop.mp4.asset.json";
import couplesMobile from "@/assets/intro/couples-mobile.mp4.asset.json";
import familyDesktop from "@/assets/intro/family-desktop.mp4.asset.json";
import familyMobile from "@/assets/intro/family-mobile.mp4.asset.json";
import { getIntroPageContent } from "@/lib/intro.functions";

const VIDEO_BY_SLUG: Record<string, { desktop: string; mobile: string }> = {
  couples: { desktop: couplesDesktop.url, mobile: couplesMobile.url },
  family: { desktop: familyDesktop.url, mobile: familyMobile.url },
};

const DEFAULT_WORDS = [
  "each other",
  "stillness",
  "unspoiled dark skies",
  "relaxation",
  "adventure",
  "nature",
  "luxury",
];

export default function IntroPage({ experienceSlug }: { experienceSlug?: string | null } = {}) {
  const fetchContent = useServerFn(getIntroPageContent);
  const { data } = useQuery({
    queryKey: ["intro-content", experienceSlug ?? null],
    queryFn: () =>
      experienceSlug
        ? fetchContent({ data: { experience_slug: experienceSlug } })
        : fetchContent(),
  });
  const WORDS = useMemo(
    () =>
      data?.rotating_words && data.rotating_words.length > 0
        ? data.rotating_words
        : DEFAULT_WORDS,
    [data?.rotating_words],
  );
  const LONGEST = useMemo(
    () => WORDS.reduce((a, b) => (b.length > a.length ? b : a), ""),
    [WORDS],
  );

  const [idx, setIdx] = useState(0);

  const x = useMotionValue(0);
  const rediscoverRef = useRef<HTMLSpanElement | null>(null);
  const slotRef = useRef<HTMLSpanElement | null>(null);
  const wordRef = useRef<HTMLSpanElement | null>(null);
  const [wordWidth, setWordWidth] = useState(0);
  const [gap, setGap] = useState(0);
  const [slotWidth, setSlotWidth] = useState(0);
  // Refs used by the animation loop so it never has to restart on re-render.
  const coverXRef = useRef(0);
  const wordWidthRef = useRef(0);

  // Measure positions whenever the word changes or the viewport resizes.
  useLayoutEffect(() => {
    const measure = () => {
      const r = rediscoverRef.current;
      const s = slotRef.current;
      const w = wordRef.current;
      if (!r || !s || !w) return;
      const rRect = r.getBoundingClientRect();
      const sRect = s.getBoundingClientRect();
      const wRect = w.getBoundingClientRect();
      const g = sRect.left - rRect.right + x.get();
      setGap(g);
      setSlotWidth(sRect.width);
      setWordWidth(wRect.width);
      wordWidthRef.current = wRect.width;
      coverXRef.current = Math.max(g + sRect.width, 0);
    };
    measure();
    if (typeof document !== "undefined" && (document as any).fonts?.ready) {
      (document as any).fonts.ready.then(measure).catch(() => {});
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [idx, x]);

  const clipPath = useTransform(x, (val) => {
    const swept = Math.min(Math.max(val - gap, 0), wordWidth || slotWidth);
    return `inset(0 0 0 ${swept}px)`;
  });

  // Single, self-contained animation loop driven by refs so React re-renders
  // (caused by setIdx or remeasures) never restart or cancel it.
  useEffect(() => {
    let cancelled = false;
    const SLIDE_MS = 1600;
    const HOLD_MS = 1400;

    const waitForCover = async () => {
      while (!cancelled && coverXRef.current <= 0) await wait(50);
    };

    const loop = async () => {
      await waitForCover();
      await wait(HOLD_MS);
      while (!cancelled) {
        const target = coverXRef.current;
        await animate(x, target, { duration: SLIDE_MS / 1000, ease: [0.65, 0, 0.35, 1] }).finished;
        if (cancelled) return;
        setIdx((i) => (i + 1) % WORDS.length);
        await wait(80);
        if (cancelled) return;
        await animate(x, 0, { duration: SLIDE_MS / 1000, ease: [0.65, 0, 0.35, 1] }).finished;
        if (cancelled) return;
        await wait(HOLD_MS);
      }
    };

    loop();
    return () => {
      cancelled = true;
    };
  }, [x]);


  // Only show video when a path has been picked. The generic banner video
  // is never actually seen (users pick a path within a second or two), so
  // skipping it saves several MB on first paint. The poster image covers
  // the chooser background.
  const pathVariant = experienceSlug ? VIDEO_BY_SLUG[experienceSlug] : null;
  const variant = pathVariant ?? { desktop: bannerDesktop.url, mobile: bannerMobile.url };
  const showVideo = pathVariant != null;
  const videoKey = experienceSlug ?? "default";
  const desktopVideoRef = useRef<HTMLVideoElement | null>(null);
  const mobileVideoRef = useRef<HTMLVideoElement | null>(null);
  // Decide once on mount which video to render. Avoids both files being
  // fetched on every device (autoPlay triggers a real download even on the
  // `hidden md:block` element). SSR renders neither; the client decides on
  // hydration. The img poster covers the gap.
  // On save-data / 2g / 3g connections we skip the video entirely and keep
  // the poster, so the page doesn't stall on a 2 MB MP4 over cellular.
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  const [allowVideo, setAllowVideo] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 768px)");
    setIsDesktop(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", onChange);
    const conn = (navigator as any).connection;
    if (conn) {
      const slow =
        !!conn.saveData ||
        conn.effectiveType === "slow-2g" ||
        conn.effectiveType === "2g" ||
        conn.effectiveType === "3g";
      setAllowVideo(!slow);
    }
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const tryPlay = (v: HTMLVideoElement | null) => {
      if (!v) return;
      v.muted = true;
      v.defaultMuted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay(desktopVideoRef.current);
    tryPlay(mobileVideoRef.current);
  }, [videoKey, isDesktop]);

  return (
    <div
      className="relative w-full h-full overflow-hidden bg-background text-foreground"
    >
      {/* LCP poster image — explicit dimensions and high fetch priority so
          the browser paints it first. Sits beneath the videos and is hidden
          once playback covers the frame. */}
      <img
        src={bannerPoster.url}
        alt=""
        width={1600}
        height={900}
        decoding="async"
        // @ts-expect-error fetchpriority is a valid HTML attr
        fetchpriority="high"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Background video — fills the (max 1600px) container.
          No `poster` attribute: iOS/Safari renders a tap-to-play overlay on
          the poster image before the video element starts playing. The poster
          image is rendered as a separate <img> beneath the video instead, so
          the video element has nothing to overlay a play button onto. */}
      {showVideo && allowVideo && isDesktop === true && (
        <video
          ref={desktopVideoRef}
          key={`desktop-${videoKey}`}
          className="absolute inset-0 w-full h-full object-cover"
          src={variant.desktop}
          autoPlay
          muted
          loop
          playsInline
          controls={false}
          disablePictureInPicture
          preload="metadata"
        />
      )}
      {showVideo && allowVideo && isDesktop === false && (
        <video
          ref={mobileVideoRef}
          key={`mobile-${videoKey}`}
          className="absolute inset-0 w-full h-full object-cover"
          src={variant.mobile}
          autoPlay
          muted
          loop
          playsInline
          controls={false}
          disablePictureInPicture
          preload="metadata"
        />
      )}

      {/* Cinematic overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/5 to-black/70" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_55%,rgba(0,0,0,0.3)_100%)]" />

      {/* Lower-third: Rediscover + rotating word */}
      <div className="relative z-10 h-full w-full flex items-center px-6 sm:px-10 md:px-14">
        <h1 className="sr-only">Rediscover {WORDS.join(", ")}</h1>

        <span
          aria-hidden
          className="relative inline-flex items-baseline whitespace-nowrap font-serif text-foreground text-shadow-soft text-2xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight font-normal"
          style={{ willChange: "transform", transform: "translate3d(0,0,0)" }}
        >
          {/* Layout sizer reserves width for "Rediscover " + LONGEST */}
          <span className="invisible">Rediscover&nbsp;&nbsp;{LONGEST}</span>

          {/* Slot for rotating word — width = LONGEST */}
          <span ref={slotRef} className="absolute top-0 right-0 inline-block text-left">
            <span className="invisible">{LONGEST}</span>
            <motion.span
              ref={wordRef}
              style={{ clipPath, WebkitClipPath: clipPath } as any}
              className="absolute left-0 top-0 inline-block text-foreground"
            >
              {WORDS[idx % WORDS.length]}
            </motion.span>
          </span>

          {/* "Rediscover" overlay — slides across, its right edge is the wipe */}
          <motion.span
            ref={rediscoverRef}
            style={{ x }}
            className="absolute left-0 top-0 z-10"
          >

            Rediscover
          </motion.span>
        </span>
      </div>
    </div>
  );
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
